import { ContentTab } from '@src/types.ts/general';
import { isAudioThenVideoEnabled } from '@src/utility/audioThenVideoFlag';
import { getAiDescriptionResponse } from '@src/utility/story';

import { getAllClips } from './StoryManagerFolderView/clipDisplay';

import type { NewStoryUpdate } from '../NewStoriesQuerySubscriptionComponent';
import type { AlbumStory, DashboardStory } from '@src/types.ts/story';

/**
 * The Content Studio link for a story. `tab` names the tab to open on (a
 * STORY open passes one: see `getStoryOpenTab`, and the upload toast always
 * lands on Transcript & Speakers per PLAT-11934); without it Content Studio
 * keeps its own default, which is what a clip open wants (the Video Clips grid
 * with that clip's preview).
 */
export const buildTargetUrl = (
  params: URLSearchParams,
  storyId: string,
  playbackId: string | undefined,
  tab?: ContentTab,
) =>
  `?storyId=${storyId}&env=${params.get('env')}&playbackId=${playbackId || ''}${
    tab ? `&tab=${encodeURIComponent(tab)}` : ''
  }`;

/**
 * The tab a story opened from the Story Manager or Home Recent Stories lands
 * on: Video Clips when it already has non-hidden clips (the set the Video
 * Clips grid shows), otherwise Transcript & Languages, where work on a fresh
 * story starts (PLAT-11934).
 */
export const getStoryOpenTab = (story: DashboardStory): ContentTab =>
  getAllClips(story).length > 0
    ? ContentTab.video_clip
    : ContentTab.transcript_speakers;

/**
 * `params` without a deleted story's context, or null when the URL doesn't
 * point at one of `deletedStoryIds`. A deleted story's `storyId` fails the
 * next access check and redirects the user away (PLAT-11622); `showcase`
 * keeps the org resolvable without it.
 */
export const dropDeletedStoryParams = (
  params: URLSearchParams,
  deletedStoryIds: string[],
  showcaseSlug: string,
): URLSearchParams | null => {
  const storyId = params.get('storyId');
  if (!storyId || !deletedStoryIds.includes(storyId)) return null;
  const next = new URLSearchParams(params);
  next.delete('storyId');
  next.delete('playbackId');
  next.delete('videoId');
  if (!next.get('showcase')) {
    next.set('showcase', showcaseSlug);
  }
  return next;
};

export const hasUploadFailed = (s: NewStoryUpdate): boolean =>
  s.useAws
    ? !!s.uploadLog?.handleFailure
    : !!s.uploadLog?.handleFailure &&
      s.uploadLog?.initialUpload?.status !== 'success';

export const isVideoUploadStuck = (s: NewStoryUpdate): boolean =>
  s.uploadLog?.initialUpload?.status !== 'success' &&
  Date.now() - new Date(s.uploadLog?.initialUpload?.updatedAt).getTime() >
    1500000;

export const isStoryOriginalVideoReady = (s: NewStoryUpdate): boolean =>
  !s.useAws
    ? !!s.originalVideo?.video?.thumbnailUrl
    : s.uploadLog?.ffmpegProcessing?.status === 'success';

export const isStoryTranscriptionReady = (s: NewStoryUpdate): boolean =>
  s.useAws
    ? s.uploadLog?.combineResults?.status === 'success'
    : ['FAILED', 'COMPLETED'].includes(s.transcription?.jobStatus);

export const isStoryCompressedVideoReady = (s: NewStoryUpdate): boolean =>
  s.useAws
    ? s.uploadLog?.extractThumbnailsFromLowRes?.status === 'success' ||
      s.uploadLog?.compressVideo?.output?.jobs?.some(
        (j: any) =>
          j &&
          j.resolution === '270' &&
          (j.status || '').toLowerCase() === 'complete',
      )
    : !!s.originalVideo?.video;

/**
 * The extracted frames exist — what speaker frame analysis reads. Stricter
 * than isStoryCompressedVideoReady on AWS: a finished 270p is playable before
 * its thumbnails are extracted from it.
 */
export const isStoryThumbnailsReady = (s: NewStoryUpdate): boolean =>
  s.useAws
    ? s.uploadLog?.extractThumbnailsFromLowRes?.status === 'success'
    : !!s.originalVideo?.video;

export const isStoryWaveformReady = (s: NewStoryUpdate): boolean =>
  s.useAws ? s.uploadLog?.generateWaveform?.status === 'success' : true;

export const isStoryProcessingStarted = (s: NewStoryUpdate): boolean =>
  s.uploadLog?.initialUpload?.status === 'success';

export const calculateCompletionPercent = (story: AlbumStory): number => {
  if (isStoryReady(story)) {
    return 100;
  }
  if (isStoryOriginalVideoReady(story)) {
    return 75;
  }
  if (isStoryProcessingStarted(story)) {
    return 50;
  }
  return Math.round((story.uploadLog?.initialUpload?.progress || 0) / 2);
};

export const getDescription = (story: AlbumStory) =>
  getAiDescriptionResponse(story)?.response?.toString() || '';

export const isStoryReady = (s: NewStoryUpdate): boolean =>
  isStoryOriginalVideoReady(s) &&
  isStoryTranscriptionReady(s) &&
  isStoryCompressedVideoReady(s);

// PLAT-11224 (Faster TTFE): editor entry unlocks at transcription-ready
// (`combine_results`) instead of the full isStoryReady AND-gate — the
// transcript/timeline are editable minutes before video renditions exist.
// Row clicks and list clickability key off this; isStoryReady remains the
// full-pipeline signal (completion %, upload chip, toaster).
// Kill switch (PLAT-11222): with audio-then-video off, entry waits for the
// full pipeline again.
export const isStoryEditorEntryReady = (s: NewStoryUpdate): boolean =>
  isAudioThenVideoEnabled() ? isStoryTranscriptionReady(s) : isStoryReady(s);

export const isStoryLoading = (s: AlbumStory): boolean =>
  s.externalUploadStatus !== 'FAILED' &&
  !hasUploadFailed(s) &&
  (!isStoryOriginalVideoReady(s) ||
    !isStoryTranscriptionReady(s) ||
    !isStoryCompressedVideoReady(s));

const UPLOAD_STUCK_THRESHOLD = 10 * 60 * 1000; // 10 minutes

export const isStoryAbandoned = (s: AlbumStory): boolean => {
  if (s.externalUploadStatus === 'CANCELLED') return true;
  if (
    s.externalUploadStatus === 'COMPLETED' ||
    s.externalUploadStatus === 'FAILED'
  ) {
    return false;
  }
  if (s.uploadLog?.initialUpload?.status === 'success') return false;

  const lastUploadActivity = s.uploadLog?.initialUpload?.updatedAt;
  if (lastUploadActivity) {
    return (
      Date.now() - new Date(lastUploadActivity).getTime() >
      UPLOAD_STUCK_THRESHOLD
    );
  }
  // No upload activity — fall back to story creation time
  if (s._createdAt) {
    return (
      Date.now() - new Date(s._createdAt).getTime() > UPLOAD_STUCK_THRESHOLD
    );
  }
  return false;
};

/** Upload never completed — file didn't reach S3. Retryable. */
export const isUploadFailure = (s: NewStoryUpdate): boolean =>
  hasUploadFailed(s) && s.uploadLog?.initialUpload?.status !== 'success';

/** Upload succeeded but backend processing failed. Not yet retryable. */
export const isProcessingFailure = (s: NewStoryUpdate): boolean =>
  hasUploadFailed(s) && s.uploadLog?.initialUpload?.status === 'success';

/**
 * Whether a story should appear in dashboard listings (Recent table and the
 * Folder view's unfoldered list at root). Both views must agree on this so a
 * story doesn't disappear from one and re-appear in the other.
 *
 * Internal users see in-progress / failed stories; external users only see
 * stories with at least one produced clip.
 */
export const isDashboardVisibleStory = (
  story: DashboardStory,
  opts: { isInternalUser: boolean },
): boolean =>
  story.externalUploadStatus !== 'CANCELLED' &&
  (story.byExternalUser ||
    ((!!story.finalVideo || !!story.originalVideo || isStoryLoading(story)) &&
      (opts.isInternalUser || story.otherVideos.length > 0)));

export const getStoryNameFromFileName = (
  fileName: string,
  maxLength: number,
) => {
  return (
    fileName
      //remove extension
      .replace(/\.[^.]*$/, '')
      //replace underscores with spaces
      .replace(/[_\-.]/g, ' ')
      // insert a space between lower & upper
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // space before last upper in a sequence followed by lower
      .replace(/\b([A-Z]+)([A-Z])([a-z])/, '$1 $2$3')
      // replace multiple spaces with single space
      .replace(/\s+/g, ' ')
      // limit to 120 characters
      .substring(0, maxLength)
      // trim whitespace
      .trim()
  );
};
